import { Component } from '@theme/component';
import { RecentlyViewed } from '@theme/recently-viewed-products';

/**
 * Loads native product-card markup for the products Horizon already records locally.
 *
 * @typedef {{ results: HTMLElement }} Refs
 * @extends {Component<Refs>}
 */
class BroskeRecentlyViewed extends Component {
  requiredRefs = ['results'];

  connectedCallback() {
    super.connectedCallback();
    this.#load();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    super.disconnectedCallback();
  }

  /** @type {AbortController | undefined} */
  #abortController;

  async #load() {
    const currentProductId = this.dataset.currentProductId;
    const productIds = RecentlyViewed.getProducts().filter((id) => id !== currentProductId).slice(0, 4);

    if (productIds.length === 0 || !this.dataset.searchUrl) return;

    this.#abortController?.abort();
    this.#abortController = new AbortController();

    const url = new URL(this.dataset.searchUrl, window.location.origin);
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('resources[limit]', '4');
    url.searchParams.set('q', productIds.map((id) => `id:${id}`).join(' OR '));

    try {
      const response = await fetch(url, { signal: this.#abortController.signal });
      if (!response.ok) return;

      const payload = await response.json();
      const products = payload?.resources?.results?.products;
      if (!Array.isArray(products) || products.length === 0) return;

      const productsById = new Map(products.map((product) => [String(product.id), product]));
      const cards = productIds.map((id) => productsById.get(id)).filter(Boolean).map(this.#createCard);
      if (cards.length === 0) return;

      this.refs.results.replaceChildren(...cards);
      this.hidden = false;
    } catch (error) {
      if (!(error instanceof DOMException && error.name === 'AbortError')) {
        console.error('Unable to load recently viewed products.', error);
      }
    }
  }

  /**
   * @param {{ id: number, title: string, url: string, price: string, featured_image?: { url?: string, alt?: string, width?: number, height?: number } }} product
   * @returns {HTMLElement}
   */
  #createCard(product) {
    const article = document.createElement('article');
    article.className = 'broske-recently-viewed__card';

    const link = document.createElement('a');
    link.href = product.url;
    link.className = 'broske-recently-viewed__link';

    if (product.featured_image?.url) {
      const image = document.createElement('img');
      image.src = product.featured_image.url;
      image.alt = product.featured_image.alt || product.title;
      image.loading = 'lazy';
      image.className = 'broske-recently-viewed__image';
      if (product.featured_image.width) image.width = product.featured_image.width;
      if (product.featured_image.height) image.height = product.featured_image.height;
      link.append(image);
    }

    const title = document.createElement('h3');
    title.textContent = product.title;
    title.className = 'broske-recently-viewed__title';

    const price = document.createElement('p');
    price.textContent = product.price;
    price.className = 'broske-recently-viewed__price';

    link.append(title, price);
    article.append(link);
    return article;
  }
}

if (!customElements.get('broske-recently-viewed')) {
  customElements.define('broske-recently-viewed', BroskeRecentlyViewed);
}
